import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react';
import Map, { Layer, Source } from 'react-map-gl/maplibre'
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre'
import type { LngLatBoundsLike } from 'maplibre-gl'
import { useSyncedViewState } from '../../hooks/useSyncedViewState'
import type { SyncedView } from '../../hooks/useSyncedViewState'
import { getMapStyle } from '../../lib/mapConfig'

export interface ComparePane {
  label: string
  data: GeoJSON.FeatureCollection | null
  fillColor: unknown[] | null
  legend?: React.ReactNode
}

interface GeoCompareMapProps {
  paneA: ComparePane
  paneB: ComparePane
  opacity: number
  initialView: SyncedView
  flyToBounds?: LngLatBoundsLike | null
  onUnitClick?: (unitId: string) => void
}

// Each pane owns its own geojson source; the same unit id is highlighted in BOTH
// panes by writing feature-state to each source. promoteId="id" makes the unit's
// properties.id the feature id that setFeatureState targets.
const PANE_SOURCE = { a: 'compare-source-a', b: 'compare-source-b' } as const

/**
 * Desktop-only side-by-side compare (spec §3.5): two panes, one shared viewState
 * (pan/zoom locked), independent metric layers, hover highlights the same unit
 * in both panes. Panes render independently — one erroring/empty never blanks
 * the other.
 *
 * Hover is driven imperatively via maplibre feature-state (not React state) and
 * throttled to one update per animation frame. A fast cursor therefore never
 * re-renders React or either <Map>, so the highlight can't fall behind the mouse.
 */
export function GeoCompareMap({ paneA, paneB, opacity, initialView, flyToBounds, onUnitClick }: GeoCompareMapProps) {
  const { viewState, onMove } = useSyncedViewState(initialView)

  const mapARef = useRef<MapRef>(null)
  const mapBRef = useRef<MapRef>(null)
  const hoverIdRef = useRef<string | null>(null)
  const pendingRef = useRef<string | null>(null)
  const rafRef = useRef<number | null>(null)

  const [mapLoaded, setMapLoaded] = useState(false)

  // Fit pane A to the bounds; the synced onMove propagates the same view to pane B.
  useEffect(() => {
    if (!flyToBounds || !mapLoaded) return
    mapARef.current?.getMap()?.fitBounds(flyToBounds, { padding: 60, maxZoom: 15, duration: 1200 })
  }, [flyToBounds, mapLoaded])

  const handleClick = useCallback((e: MapLayerMouseEvent) => {
    const feature = e.features?.find(f => f.layer.id.startsWith('compare-fill'))
    const id = feature?.properties?.id as string | undefined
    if (id && onUnitClick) onUnitClick(id)
  }, [onUnitClick])

  // Move the `hover` feature-state from the old unit to the new one on every map
  // that currently has its source loaded. Cheap: touches at most two features.
  const applyHover = useCallback((nextId: string | null) => {
    if (nextId === hoverIdRef.current) return
    const entries: [React.RefObject<MapRef | null>, string][] = [
      [mapARef, PANE_SOURCE.a],
      [mapBRef, PANE_SOURCE.b],
    ]
    for (const [ref, source] of entries) {
      const map = ref.current?.getMap()
      if (!map || !map.getSource(source)) continue
      if (hoverIdRef.current != null) map.removeFeatureState({ source, id: hoverIdRef.current }, 'hover')
      if (nextId != null) map.setFeatureState({ source, id: nextId }, { hover: true })
    }
    hoverIdRef.current = nextId
  }, [])

  const scheduleHover = useCallback((id: string | null) => {
    pendingRef.current = id
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      applyHover(pendingRef.current)
    })
  }, [applyHover])

  const handleMouseMove = useCallback((e: MapLayerMouseEvent) => {
    const feature = e.features?.find(f => f.layer.id.startsWith('compare-fill'))
    scheduleHover((feature?.properties?.id as string) ?? null)
  }, [scheduleHover])

  const handleMouseLeave = useCallback(() => scheduleHover(null), [scheduleHover])

  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
  }, [])

  const renderPane = (pane: ComparePane, key: 'a' | 'b') => (
    <Box flex={1} position="relative" minW={0}>
      <Map
        ref={key === 'a' ? mapARef : mapBRef}
        {...viewState}
        onMove={onMove}
        onLoad={() => setMapLoaded(true)}
        mapStyle={getMapStyle()}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={pane.data ? [`compare-fill-${key}`] : []}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {pane.data && pane.fillColor && (
          <Source id={PANE_SOURCE[key]} type="geojson" data={pane.data} promoteId="id">
            <Layer id={`compare-fill-${key}`} type="fill"
              paint={{ 'fill-color': pane.fillColor as any, 'fill-opacity': opacity }} />
            <Layer id={`compare-line-${key}`} type="line"
              paint={{ 'line-color': '#94A3B8', 'line-width': 0.5 }} />
            <Layer id={`compare-hover-${key}`} type="line"
              paint={{
                'line-color': '#1A202C',
                'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2, 0] as any,
              }} />
          </Source>
        )}
      </Map>
      <Flex position="absolute" top={2} left={2} bg="whiteAlpha.900" px={2} py={1}
        borderRadius="md" boxShadow="sm" direction="column" gap={1} maxW="240px">
        <Text fontSize="xs" fontWeight="bold">{pane.label}</Text>
        {pane.legend}
      </Flex>
    </Box>
  )

  return (
    <Flex h="full" gap="2px">
      {renderPane(paneA, 'a')}
      {renderPane(paneB, 'b')}
    </Flex>
  )
}
